import { promises as fs } from 'node:fs';
import { isAbsolute, join, relative } from 'node:path';
import type { LogEntry } from './types';
import { matchesPathPattern } from './helper/path-matcher';

/**
 * Resolve log directory path
 */
export function resolveLogDir(provided?: string): string {
  if (!provided) {
    return join(process.cwd(), 'logs');
  }
  return isAbsolute(provided) ? provided : join(process.cwd(), provided);
}

/**
 * Get relative path from current working directory
 */
export function getRelativePath(filePath: string): string {
  return relative(process.cwd(), filePath);
}

/**
 * Check if file exists
 */
export async function fileExists(filePath: string): Promise<boolean> {
  try {
    await fs.access(filePath);
    return true;
  } catch {
    return false;
  }
}

/**
 * Parse a log line as JSON
 */
export function parseLogLine(line: string): LogEntry | undefined {
  const trimmed = line.trim();
  if (!trimmed) return undefined;

  try {
    return JSON.parse(trimmed) as LogEntry;
  } catch {
    return undefined;
  }
}

/**
 * Extract number from message using regex pattern
 */
export function extractNumber(message: string, pattern: RegExp): number | undefined {
  if (typeof message !== 'string') return undefined;

  const match = message.match(pattern);
  if (!match) return undefined;

  const value = Number(match[1]);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * Parse limit parameter with default and max value
 */
export function parseLimit(value: string | undefined, defaultValue: number, maxValue: number): number {
  if (typeof value !== 'string' || !value.trim()) {
    return defaultValue;
  }

  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.min(Math.floor(parsed), maxValue);
  }

  return defaultValue;
}

/**
 * Parse positive integer with fallback
 */
export function parsePositiveInt(value: string | undefined, fallback: number): number {
  if (typeof value !== 'string' || !value.trim()) {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
}

/**
 * Resolve log file path with security checks
 */
export function resolveLogFilePath(baseDir: string, fileName: string): string {
  const sanitized = fileName.replace(/\\/g, '/');
  const segments = sanitized.split('/').filter(Boolean);

  if (segments.some((segment) => segment === '..')) {
    throw new Error('Invalid log file path');
  }

  const resolved = join(baseDir, segments.join('/'));
  const rel = relative(baseDir, resolved);

  if (rel.startsWith('..')) {
    throw new Error('Access to the specified log file is denied');
  }

  return resolved;
}

/**
 * Check if actual request path matches expected OpenAPI/Swagger path pattern
 *
 * @param actualPath - The actual request path from logs (e.g., /api/users/123)
 * @param pattern - The OpenAPI path pattern to match against (e.g., /api/users/{id})
 * @returns true if the path matches the pattern
 *
 * @example
 * matchesPath('/api/users/123', '/api/users/{id}') // true
 * matchesPath('/api/users/123', '/api/users') // true (prefix match)
 * matchesPath('/api/posts/456', '/api/users/{id}') // false
 */
export function matchesPath(actualPath: string | undefined, pattern: string): boolean {
  return matchesPathPattern(actualPath, pattern);
}

/**
 * Check if actual request method matches expected HTTP method
 *
 * @param actualMethod - The actual request method from logs (e.g., 'GET', 'POST')
 * @param expectedMethod - The expected HTTP method to match against (e.g., 'GET')
 * @returns true if the method matches (case-insensitive)
 *
 * @example
 * matchesMethod('GET', 'GET') // true
 * matchesMethod('get', 'GET') // true
 * matchesMethod('POST', 'GET') // false
 * matchesMethod(undefined, 'GET') // false
 */
export function matchesMethod(actualMethod: string | undefined, expectedMethod: string): boolean {
  if (!actualMethod || !expectedMethod) {
    return false;
  }
  return actualMethod.toUpperCase() === expectedMethod.toUpperCase();
}


/**
 * Serialize error for JSON response
 */
export function serializeError(error: unknown): { name?: string; message: string } {
  return error instanceof Error
    ? { name: error.name, message: error.message }
    : { message: String(error) };
}
